// Sistema i18n mínimo para Golf Divino
// - Contexto React con idioma actual (es | en | fr)
// - Persistencia en localStorage
// - Sincronización con prefijo de URL (/, /en/, /fr/)
// - Helper t() para leer campos de la BD con estructura { es, en, fr } o string legacy
// - Diccionario de strings de UI (menú, botones, etc.)

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export const LANGS = ['es', 'en', 'fr'];
export const DEFAULT_LANG = 'es';
export const LANG_META = {
  es: { flag: '🇪🇸', label: 'Español', short: 'ES' },
  en: { flag: '🇬🇧', label: 'English', short: 'EN' },
  fr: { flag: '🇫🇷', label: 'Français', short: 'FR' },
};

// UI strings (static)
const UI = {
  es: {
    'nav.home': 'Inicio',
    'nav.quienes': 'Quiénes Somos',
    'nav.ofrecemos': 'Qué Ofrecemos',
    'nav.experiencias': 'Experiencias',
    'nav.colaboradores': 'Colaboradores',
    'nav.noticias': 'Noticias',
    'nav.contacto': 'Contacto',
    'nav.follow_us': 'Síguenos',
    'nav.newsletter': 'Newsletter',
    'btn.more_info': 'Más información',
    'btn.read_more': 'Leer más',
    'btn.read': 'Leer',
    'btn.book': 'Reservar plaza',
    'btn.send': 'Enviar',
    'btn.send_msg': 'Enviar mensaje',
    'btn.subscribe': 'Suscribirme',
    'btn.contact': 'Contacto',
    'btn.view_all': 'Ver todas',
    'btn.view_all_news': 'Ver todas las noticias',
    'btn.view_all_partners': 'Ver todos',
    'btn.view_full_calendar': 'Ver calendario completo',
    'btn.view_details': 'Ver ficha',
    'btn.record': 'Ficha',
    'btn.back': 'Volver',
    'btn.back_to_calendar': 'Volver al calendario',
    'btn.back_to_news': 'Volver a noticias',
    'btn.previous': 'Anterior',
    'btn.next': 'Siguiente',
    'btn.what_we_offer': 'Qué ofrecemos',
    'btn.calendar_2026': 'Calendario 2026',
    'form.name': 'Nombre y apellidos',
    'form.email': 'Email',
    'form.phone': 'Teléfono',
    'form.message': 'Mensaje',
    'form.handicap': 'Hándicap',
    'form.notes': 'Notas',
    'form.location': 'Ubicación',
    'form.newsletter': 'Quiero recibir la newsletter',
    'form.your_email': 'Tu email',
    'form.sending': 'Enviando...',
    'form.thanks': '¡Gracias! Te contactaremos pronto.',
    'exp.status_open': 'Inscripción abierta',
    'exp.status_completo': 'Completo',
    'exp.status_celebrado': 'Celebrado',
    'exp.status_draft': 'Próximamente',
    'exp.inscribirse': 'Inscribirse',
    'exp.contents': 'Contenidos del evento',
    'exp.date': 'Fecha',
    'exp.venue': 'Sede',
    'exp.schedule': 'Horarios',
    'exp.experience': 'La experiencia',
    'exp.registration': 'Inscripción',
    'exp.sign_up': 'Inscríbete',
    'exp.event_info': 'Información del evento',
    'exp.confirm_contact': 'Te contactaremos por email para confirmar plaza.',
    'home.club_eyebrow': 'Club Social y de Negocios',
    'home.welcome': 'Bienvenido a Golf Divino',
    'home.offer_eyebrow': 'Qué ofrecemos',
    'home.offer_script': 'Una experiencia',
    'home.offer_title': 'Todo lo que un amante del golf y del vino necesita',
    'home.cal_eyebrow': 'Calendario 2026',
    'home.cal_script': 'Torneos y',
    'home.cal_title': 'Experiencias del año',
    'home.cal_sub': 'Una agenda cuidadosamente seleccionada de torneos y catas por toda España.',
    'home.team_eyebrow': 'Quiénes Somos',
    'home.team_script': 'El equipo',
    'home.team_title': 'Detrás de Golf Divino',
    'home.team_sub': 'Tres pasiones unidas por un mismo propósito: crear momentos inolvidables.',
    'home.news_eyebrow': 'Actualidad',
    'home.news_script': 'Últimas',
    'home.news_title': 'Noticias del club',
    'home.news_sub': 'Mantente al día de todo lo que ocurre en Golf Divino.',
    'home.partners_eyebrow': 'Colaboradores',
    'home.partners_title': 'Nuestros aliados',
    'home.partners_sub': 'Bodegas, marcas y patrocinadores que hacen posible cada experiencia.',
    'home.wineries': 'Bodegas asociadas',
    'home.other_partners': 'Otros asociados',
    'home.sponsors': 'Patrocinadores',
    'about.eyebrow': 'Quiénes Somos',
    'about.title': 'Nuestra historia',
    'about.sub': 'Tres pasiones, un único club',
    'offer.eyebrow': 'Qué ofrecemos',
    'offer.title': 'Experiencias',
    'offer.sub': 'Diseñadas para amantes del golf y del vino',
    'experiences.title': 'Experiencias',
    'experiences.sub': 'Una agenda de momentos únicos',
    'partners.eyebrow': 'Colaboradores',
    'partners.title': 'Nuestros aliados',
    'partners.sub': 'Bodegas, marcas y patrocinadores',
    'contact.eyebrow': 'Contacto',
    'contact.title': 'Hablemos',
    'contact.sub': 'Estamos a tu disposición',
    'contact.reach_us': 'Contáctanos',
    'news.eyebrow': 'Actualidad',
    'news.title': 'Noticias',
    'news.sub': 'Últimas novedades del club',
    'news.loading': 'Cargando...',
    'news.empty': 'Aún no hay noticias publicadas.',
    'news.featured': 'Destacada',
    'news.other': 'Otras noticias',
    'footer.nav': 'Navegación',
    'footer.contact': 'Contacto',
    'footer.newsletter': 'Newsletter',
    'footer.legal': 'Aviso Legal',
    'footer.privacy': 'Privacidad',
    'footer.cookies': 'Cookies',
    'footer.rights': 'Todos los derechos reservados',
    'newsletter.title': 'Suscríbete a nuestra newsletter',
    'newsletter.subtitle': 'Recibe información exclusiva sobre nuestras próximas experiencias.',
  },
  en: {
    'nav.home': 'Home',
    'nav.quienes': 'About Us',
    'nav.ofrecemos': 'What We Offer',
    'nav.experiencias': 'Experiences',
    'nav.colaboradores': 'Partners',
    'nav.noticias': 'News',
    'nav.contacto': 'Contact',
    'nav.follow_us': 'Follow us',
    'nav.newsletter': 'Newsletter',
    'btn.more_info': 'More info',
    'btn.read_more': 'Read more',
    'btn.read': 'Read',
    'btn.book': 'Book your spot',
    'btn.send': 'Send',
    'btn.send_msg': 'Send message',
    'btn.subscribe': 'Subscribe',
    'btn.contact': 'Contact',
    'btn.view_all': 'View all',
    'btn.view_all_news': 'View all news',
    'btn.view_all_partners': 'View all',
    'btn.view_full_calendar': 'View full calendar',
    'btn.view_details': 'View details',
    'btn.record': 'Details',
    'btn.back': 'Back',
    'btn.back_to_calendar': 'Back to calendar',
    'btn.back_to_news': 'Back to news',
    'btn.previous': 'Previous',
    'btn.next': 'Next',
    'btn.what_we_offer': 'What We Offer',
    'btn.calendar_2026': '2026 Calendar',
    'form.name': 'Full name',
    'form.email': 'Email',
    'form.phone': 'Phone',
    'form.message': 'Message',
    'form.handicap': 'Handicap',
    'form.notes': 'Notes',
    'form.location': 'Location',
    'form.newsletter': 'I want to receive the newsletter',
    'form.your_email': 'Your email',
    'form.sending': 'Sending...',
    'form.thanks': 'Thank you! We will contact you soon.',
    'exp.status_open': 'Registration open',
    'exp.status_completo': 'Sold out',
    'exp.status_celebrado': 'Past event',
    'exp.status_draft': 'Coming soon',
    'exp.inscribirse': 'Sign up',
    'exp.contents': 'Event contents',
    'exp.date': 'Date',
    'exp.venue': 'Venue',
    'exp.schedule': 'Schedule',
    'exp.experience': 'The experience',
    'exp.registration': 'Registration',
    'exp.sign_up': 'Sign up',
    'exp.event_info': 'Event information',
    'exp.confirm_contact': 'We will contact you by email to confirm your spot.',
    'home.club_eyebrow': 'Social & Business Club',
    'home.welcome': 'Welcome to Golf Divino',
    'home.offer_eyebrow': 'What we offer',
    'home.offer_script': 'An experience',
    'home.offer_title': 'Everything a golf and wine lover needs',
    'home.cal_eyebrow': '2026 Calendar',
    'home.cal_script': 'Tournaments &',
    'home.cal_title': 'Experiences of the year',
    'home.cal_sub': 'A carefully curated agenda of tournaments and tastings across Spain.',
    'home.team_eyebrow': 'About Us',
    'home.team_script': 'The team',
    'home.team_title': 'Behind Golf Divino',
    'home.team_sub': 'Three passions united by a single purpose: creating unforgettable moments.',
    'home.news_eyebrow': 'Current news',
    'home.news_script': 'Latest',
    'home.news_title': 'Club news',
    'home.news_sub': 'Stay up to date with everything happening at Golf Divino.',
    'home.partners_eyebrow': 'Partners',
    'home.partners_title': 'Our allies',
    'home.partners_sub': 'Wineries, brands and sponsors that make every experience possible.',
    'home.wineries': 'Associated wineries',
    'home.other_partners': 'Other partners',
    'home.sponsors': 'Sponsors',
    'about.eyebrow': 'About Us',
    'about.title': 'Our story',
    'about.sub': 'Three passions, one single club',
    'offer.eyebrow': 'What we offer',
    'offer.title': 'Experiences',
    'offer.sub': 'Designed for golf and wine lovers',
    'experiences.title': 'Experiences',
    'experiences.sub': 'An agenda of unique moments',
    'partners.eyebrow': 'Partners',
    'partners.title': 'Our allies',
    'partners.sub': 'Wineries, brands and sponsors',
    'contact.eyebrow': 'Contact',
    'contact.title': "Let's talk",
    'contact.sub': 'We are at your service',
    'contact.reach_us': 'Contact us',
    'news.eyebrow': 'Current news',
    'news.title': 'News',
    'news.sub': 'Latest club updates',
    'news.loading': 'Loading...',
    'news.empty': 'No news published yet.',
    'news.featured': 'Featured',
    'news.other': 'Other news',
    'footer.nav': 'Navigation',
    'footer.contact': 'Contact',
    'footer.newsletter': 'Newsletter',
    'footer.legal': 'Legal Notice',
    'footer.privacy': 'Privacy',
    'footer.cookies': 'Cookies',
    'footer.rights': 'All rights reserved',
    'newsletter.title': 'Subscribe to our newsletter',
    'newsletter.subtitle': 'Get exclusive information about our upcoming experiences.',
  },
  fr: {
    'nav.home': 'Accueil',
    'nav.quienes': 'Qui sommes-nous',
    'nav.ofrecemos': 'Ce que nous offrons',
    'nav.experiencias': 'Expériences',
    'nav.colaboradores': 'Partenaires',
    'nav.noticias': 'Actualités',
    'nav.contacto': 'Contact',
    'nav.follow_us': 'Suivez-nous',
    'nav.newsletter': 'Newsletter',
    'btn.more_info': "Plus d'informations",
    'btn.read_more': 'Lire la suite',
    'btn.read': 'Lire',
    'btn.book': 'Réserver ma place',
    'btn.send': 'Envoyer',
    'btn.send_msg': 'Envoyer le message',
    'btn.subscribe': "S'abonner",
    'btn.contact': 'Contact',
    'btn.view_all': 'Voir tout',
    'btn.view_all_news': 'Voir toutes les actualités',
    'btn.view_all_partners': 'Voir tous',
    'btn.view_full_calendar': 'Voir le calendrier complet',
    'btn.view_details': 'Voir la fiche',
    'btn.record': 'Fiche',
    'btn.back': 'Retour',
    'btn.back_to_calendar': 'Retour au calendrier',
    'btn.back_to_news': 'Retour aux actualités',
    'btn.previous': 'Précédent',
    'btn.next': 'Suivant',
    'btn.what_we_offer': 'Ce que nous offrons',
    'btn.calendar_2026': 'Calendrier 2026',
    'form.name': 'Nom et prénom',
    'form.email': 'Email',
    'form.phone': 'Téléphone',
    'form.message': 'Message',
    'form.handicap': 'Handicap',
    'form.notes': 'Notes',
    'form.location': 'Localisation',
    'form.newsletter': 'Je souhaite recevoir la newsletter',
    'form.your_email': 'Votre email',
    'form.sending': 'Envoi...',
    'form.thanks': 'Merci ! Nous vous contacterons bientôt.',
    'exp.status_open': 'Inscription ouverte',
    'exp.status_completo': 'Complet',
    'exp.status_celebrado': 'Événement passé',
    'exp.status_draft': 'Bientôt disponible',
    'exp.inscribirse': "S'inscrire",
    'exp.contents': "Contenus de l'événement",
    'exp.date': 'Date',
    'exp.venue': 'Lieu',
    'exp.schedule': 'Horaires',
    'exp.experience': "L'expérience",
    'exp.registration': 'Inscription',
    'exp.sign_up': 'Inscrivez-vous',
    'exp.event_info': "Informations sur l'événement",
    'exp.confirm_contact': 'Nous vous contacterons par email pour confirmer votre place.',
    'home.club_eyebrow': "Club Social et d'Affaires",
    'home.welcome': 'Bienvenue à Golf Divino',
    'home.offer_eyebrow': 'Ce que nous offrons',
    'home.offer_script': 'Une expérience',
    'home.offer_title': "Tout ce dont un amateur de golf et de vin a besoin",
    'home.cal_eyebrow': 'Calendrier 2026',
    'home.cal_script': 'Tournois et',
    'home.cal_title': "Expériences de l'année",
    'home.cal_sub': "Un agenda soigneusement sélectionné de tournois et dégustations à travers l'Espagne.",
    'home.team_eyebrow': 'Qui sommes-nous',
    'home.team_script': "L'équipe",
    'home.team_title': 'Derrière Golf Divino',
    'home.team_sub': 'Trois passions unies par un même but : créer des moments inoubliables.',
    'home.news_eyebrow': 'Actualité',
    'home.news_script': 'Dernières',
    'home.news_title': 'Actualités du club',
    'home.news_sub': 'Restez informé de tout ce qui se passe chez Golf Divino.',
    'home.partners_eyebrow': 'Partenaires',
    'home.partners_title': 'Nos alliés',
    'home.partners_sub': 'Domaines viticoles, marques et sponsors qui rendent chaque expérience possible.',
    'home.wineries': 'Domaines associés',
    'home.other_partners': 'Autres partenaires',
    'home.sponsors': 'Sponsors',
    'about.eyebrow': 'Qui sommes-nous',
    'about.title': 'Notre histoire',
    'about.sub': 'Trois passions, un seul club',
    'offer.eyebrow': 'Ce que nous offrons',
    'offer.title': 'Expériences',
    'offer.sub': 'Conçues pour les amateurs de golf et de vin',
    'experiences.title': 'Expériences',
    'experiences.sub': 'Un agenda de moments uniques',
    'partners.eyebrow': 'Partenaires',
    'partners.title': 'Nos alliés',
    'partners.sub': 'Domaines, marques et sponsors',
    'contact.eyebrow': 'Contact',
    'contact.title': 'Parlons-en',
    'contact.sub': 'Nous sommes à votre service',
    'contact.reach_us': 'Nous contacter',
    'news.eyebrow': 'Actualité',
    'news.title': 'Actualités',
    'news.sub': 'Dernières nouvelles du club',
    'news.loading': 'Chargement...',
    'news.empty': 'Aucune actualité publiée pour le moment.',
    'news.featured': 'À la une',
    'news.other': 'Autres actualités',
    'footer.nav': 'Navigation',
    'footer.contact': 'Contact',
    'footer.newsletter': 'Newsletter',
    'footer.legal': 'Mentions légales',
    'footer.privacy': 'Confidentialité',
    'footer.cookies': 'Cookies',
    'footer.rights': 'Tous droits réservés',
    'newsletter.title': 'Abonnez-vous à notre newsletter',
    'newsletter.subtitle': 'Recevez des informations exclusives sur nos prochaines expériences.',
  },
};

const LangContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (v) => v,
  ui: (key) => key,
});

/**
 * Read a translatable field from an API document.
 * Accepts:
 *  - a { es, en, fr } dict → returns the current lang, falling back to es
 *  - a plain string (legacy) → returns as-is
 *  - null/undefined → returns ''
 */
export function pick(field, lang) {
  if (field === null || field === undefined) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    return field[lang] || field[DEFAULT_LANG] || Object.values(field).find(Boolean) || '';
  }
  return String(field);
}

function detectInitialLang() {
  try {
    const path = window.location.pathname;
    const m = path.match(/^\/(en|fr)(\/|$)/);
    if (m) {
      // URL is source of truth. Sync localStorage so the axios interceptor
      // (which reads gd_lang before any React context is available) uses the
      // right lang for the very first API calls.
      try { localStorage.setItem('gd_lang', m[1]); } catch (_) { /* noop */ }
      return m[1];
    }
    // No prefix → default language. Also sync so we never serve mixed lang.
    try { localStorage.setItem('gd_lang', DEFAULT_LANG); } catch (_) { /* noop */ }
    return DEFAULT_LANG;
  } catch (_) { /* noop */ }
  return DEFAULT_LANG;
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const location = useLocation();

  // Keep URL prefix in sync with lang (full reload so data re-fetches in new lang)
  // Unused after refactor: setLang now handles URL directly to avoid re-render races.

  const setLang = useCallback((newLang) => {
    if (!LANGS.includes(newLang)) return;
    // Persist first, then trigger full reload. Do NOT call setLangState here to avoid
    // a React re-render race where the URL-sync effect below overwrites localStorage
    // back to 'es' before the browser starts navigating.
    try { localStorage.setItem('gd_lang', newLang); } catch (_) { /* noop */ }
    const path = window.location.pathname + window.location.search + window.location.hash;
    const stripped = path.replace(/^\/(en|fr)(?=\/|$)/, '') || '/';
    const target = newLang === DEFAULT_LANG ? stripped : `/${newLang}${stripped === '/' ? '' : stripped}`;
    window.location.assign(target);
  }, []);

  // When URL prefix changes (e.g. back/forward), sync state
  useEffect(() => {
    const m = location.pathname.match(/^\/(en|fr)(\/|$)/);
    const urlLang = m ? m[1] : DEFAULT_LANG;
    if (urlLang !== lang) {
      setLangState(urlLang);
      try { localStorage.setItem('gd_lang', urlLang); } catch (_) { /* noop */ }
    }
  }, [location.pathname, lang]);

  // Set <html lang> for a11y/SEO
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t: (field) => pick(field, lang),
    ui: (key) => (UI[lang] && UI[lang][key]) || (UI[DEFAULT_LANG][key]) || key,
  }), [lang, setLang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

/**
 * Build a URL that respects the current language prefix.
 * Use for navigation: buildHref('/experiencias/foo')
 */
export function useLocalizedHref() {
  const { lang } = useLang();
  return useCallback((path) => {
    if (!path || !path.startsWith('/')) return path;
    if (lang === DEFAULT_LANG) return path;
    return `/${lang}${path}`;
  }, [lang]);
}
